import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const RentalYield = () => {
  // State variables for Rental Yield calculation
  const [annualRent, setAnnualRent] = useState(0); // Annual rent amount
  const [propertyValue, setPropertyValue] = useState(0); // Property value
  const [calculatedRentalYield, setCalculatedRentalYield] = useState(0); // Calculated rental yield percentage

  // Event handlers to update state values
  const handleAnnualRentChange = (e) => setAnnualRent(parseFloat(e.target.value) || 0);
  const handlePropertyValueChange = (e) => setPropertyValue(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Rental Yield
  useEffect(() => {
    // Rental Yield = (Annual Rent / Property Value) x 100
    if (propertyValue > 0) {
      const rentalYield = (annualRent / propertyValue) * 100;
      setCalculatedRentalYield(rentalYield);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [annualRent, propertyValue]);

  return (
    <Layout title="Rental Yield Calculator" path="rental-yield">
      <section className="section-padding" id="rental-yield-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Rental Yield Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Rental Yield Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Annual Rent */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualRent" className="form-label">
                        Annual Rent ($)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Annual Rent:</strong> The total rent you receive annually from the property."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualRent"
                        placeholder="Enter annual rent"
                        value={annualRent || ""}
                        onChange={handleAnnualRentChange}
                      />
                    </div>

                    {/* Input for Property Value */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="propertyValue" className="form-label">
                        Property Value ($)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Property Value:</strong> The current market value of the property."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="propertyValue"
                        placeholder="Enter property value"
                        value={propertyValue || ""}
                        onChange={handlePropertyValueChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Calculated Rental Yield:{" "}
                        <span className="fw-bold ms-3">
                          {calculatedRentalYield === 0
                            ? "0"
                            : calculatedRentalYield.toFixed(2)}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Rental Yield Calculator
                </h3>

                {/* What is a Rental Yield Calculator? */}
                <h4 className="mt-3 mb-3">What is a Rental Yield Calculator?</h4>
                <p>
                  The **Rental Yield Calculator** helps investors determine the profitability of a rental property by calculating the annual return as a percentage of its value.
                </p>
                <ul>
                  <li><strong>Annual Rent:</strong> The total amount of rent collected from the property annually.</li>
                  <li><strong>Property Value:</strong> The market value or purchase price of the property.</li>
                </ul>

                {/* Why Use a Rental Yield Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Rental Yield Calculator?</h4>
                <p>
                  This tool is essential for real estate investors to assess the potential return on investment (ROI) from rental properties. It allows investors to compare different properties and make informed decisions based on rental profitability.
                </p>

                {/* Benefits of Using a Rental Yield Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Rental Yield Calculator</h4>
                <ul>
                  <li><strong>Assists Investors in Evaluating Property Profitability:</strong> The rental yield percentage helps investors quickly gauge the profitability of a property.</li>
                  <li><strong>Helps in Comparing Different Investment Properties:</strong> Investors can use the rental yield to compare the returns of multiple properties and choose the most profitable one.</li>
                  <li><strong>Provides Insights for Property Market Analysis:</strong> The rental yield can give a clear picture of a property's performance in the current market.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Rental Yield Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Real Estate Investors:</strong> Helps investors evaluate potential rental properties based on profitability.</li>
                  <li><strong>Landlords:</strong> Provides landlords with insights into how well their rental properties are performing financially.</li>
                  <li><strong>Property Management Firms:</strong> Assists property managers in making informed decisions for clients and optimizing rental property investments.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RentalYield;
